import React, { useState } from "react";
import { LayoutDashboard, Users, CreditCard, ChartNoAxesColumn, Database, LogOut, Heart, X, UserCog, ShieldCheck, LoaderCircle, Download } from "lucide-react";
const mainMenu = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Members", path: "/members", icon: Users },
  { label: "Membership Plans", path: "/membership-plans", icon: CreditCard },
  { label: "Address Download Plans", path: "/address-download-plans", icon: Download, },
  { label: "Payments", path: "/payments", icon: CreditCard },
  { label: "Reports", path: "/reports", icon: ChartNoAxesColumn },
];

const managementMenu = [
  { label: "Staffs", path: "/staffs", icon: UserCog },
  { label: "Assign Roles", path: "/assign-roles", icon: ShieldCheck },
  { label: "Masters", path: "/masters", icon: Database },
];

function BrandLogo() {
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex h-10 w-10 items-center justify-center text-blue-700">
        <Heart size={32} strokeWidth={1.8} />
        <Heart size={13} fill="currentColor" className="absolute text-pink-500" />
      </div>
      <div>
        <h2 className="whitespace-nowrap text-sm font-bold text-blue-800">சேலம் <span className="text-pink-600">கொங்கு</span></h2>
        <p className="text-center text-[10px] text-gray-500">திருமண தகவல் மையம்</p>
      </div>
    </div>
  );
}

function LogoutModal({ onCancel, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div role="alertdialog" aria-modal="true" aria-labelledby="logout-title" aria-describedby="logout-description"
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="p-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <LogOut size={30} />
          </div>
          <h2 id="logout-title" className="mt-4 text-2xl font-bold text-slate-800">
            Logout Confirmation
          </h2>
          <p id="logout-description" className="mt-2 text-base leading-7 text-slate-500">
            Are you sure you want to log out of the admin panel?
          </p>
        </div>
        <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-4">
          <button type="button" onClick={onCancel} disabled={loading} className="flex-1 rounded-lg border border-slate-200 bg-white 
          px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={loading} className="flex flex-1 items-center justify-center gap-2 
          rounded-lg bg-rose-600 px-4 py-3 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60">
            {loading ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut size={16} />
                Logout
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ activePath, onNavigate, mobileOpen, onClose, }) {
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const renderMenu = (items) =>
    items.map(({ label, path, icon: Icon }) => {
      const isActive = activePath === path || (path !== "/dashboard" && activePath.startsWith(`${path}/`));
      return (
        <button type="button" key={path}
          onClick={() => { onNavigate(path); onClose(); }}
          className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition ${isActive
            ? "bg-blue-50 font-semibold text-blue-600"
            : "text-slate-600 hover:bg-slate-50"
            }`}>
          <Icon size={19} />
          <span>{label}</span>
        </button>
      );
    });

  const handleLogout = () => {
    setLogoutLoading(true);
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      sessionStorage.removeItem("token");
      setLogoutOpen(false);
      window.location.replace("/login");
    } catch (error) {
      setLogoutLoading(false);
    }
  };

  const loggedInUser = JSON.parse(localStorage.getItem("user") || "null");
  const formatRole = (role) => {
    if (!role) return "Administrator";
    return role.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <>
      {mobileOpen && (
        <button type="button" aria-label="Close sidebar overlay" onClick={onClose} className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-[250px] flex-col border-r border-slate-200 bg-white transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex h-[75px] items-center justify-between border-b border-slate-200 px-4">
          <BrandLogo />
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <div className="mx-2 mt-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/70 p-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
            <ShieldCheck size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-500">Logged in as</p>
            <p className="truncate text-sm text-slate-700">{formatRole(loggedInUser?.role)}</p>
          </div>
        </div>
        <div className="mt-7 px-4 pb-2 text-xs font-semibold tracking-wider text-slate-500">MAIN MENU</div>
        <nav className="px-2">{renderMenu(mainMenu)}</nav>
        <div className="mt-5 px-4 pb-2 text-xs font-semibold tracking-wider text-slate-500">MANAGEMENT</div>
        <nav className="px-2">{renderMenu(managementMenu)}</nav>
        <div className="mt-auto border-t border-slate-100 p-3">
          <button type="button" onClick={() => setLogoutOpen(true)} className="flex w-full items-center gap-3 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600 transition hover:border-rose-200 hover:bg-rose-100">
            <LogOut size={19} />
            Logout
          </button>
          <p className="mt-3 text-center text-[10px] text-slate-400">
            Salem Kongu Matrimony Admin
          </p>
        </div>
      </aside>

      {logoutOpen && (
        <LogoutModal onCancel={() => setLogoutOpen(false)} onConfirm={handleLogout} loading={logoutLoading} />
      )}
    </>
  );
}

export default Sidebar;